import {
  Badge,
  Button,
  Card,
  Grid,
  Group,
  LoadingOverlay,
  Text,
  Alert,
  Modal,
  Stack,
  Divider,
} from "@mantine/core";
import { useEffect, useState } from "react";
import {
  getPaymentLink,
  getUserCredits,
  getActiveCreditPackages,
  getAllPPRPricing,
} from "../misc/api";
import { getToken, getUserId } from "../misc/tokens";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconInfoCircle,
  IconPlane,
  IconCalendar,
} from "@tabler/icons-react";

export const BuyTokenScreen = () => {
  const [credits, setCredits] = useState(null);
  const [visible, { toggle }] = useDisclosure(true);
  const [paymentError, setPaymentError] = useState(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [loadingPackage, setLoadingPackage] = useState(null);

  // Dynamic pricing data
  const [creditPackages, setCreditPackages] = useState([]);
  const [pprPricing, setPprPricing] = useState([]);
  const [loadingPricing, setLoadingPricing] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoadingPricing(true);

      try {
        // Load user credits
        const creditObjects = await getUserCredits();

        creditObjects.transactions?.forEach((credit) => {
          if (new Date(credit.valid) > new Date()) {
            setCredits(true);
          }
        });

        // Load pricing data from database
        const [packagesResponse, pprResponse] = await Promise.all([
          getActiveCreditPackages(),
          getAllPPRPricing(),
        ]);

        setCreditPackages(packagesResponse.data || []);
        setPprPricing(pprResponse.data || []);
      } catch (error) {
        console.error("Error loading pricing data:", error);
      } finally {
        setLoadingPricing(false);
        toggle();
      }
    };

    loadData();
  }, []);

  const getPaymentLinkAndForward = async (
    price,
    product,
    packageData = null
  ) => {
    setLoadingPackage(product);
    setPaymentError(null);

    try {
      const user = getUserId();

      // Calculate validity for subscription packages
      let valid = null;
      if (packageData?.duration_months) {
        const validDate = new Date();
        validDate.setMonth(validDate.getMonth() + packageData.duration_months);
        valid = validDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
      }

      const paymentLink = await getPaymentLink({
        cost: price,
        product,
        user,
        valid,
      });

      if (paymentLink.link) {
        window.location.href = paymentLink.link;
      } else {
        throw new Error("No payment link received");
      }
    } catch (error) {
      console.error("Payment error:", error);
      setPaymentError(error);
      setShowErrorModal(true);
    } finally {
      setLoadingPackage(null);
    }
  };

  // Convert PPR pricing to display format
  const getOneTimePurchases = () => {
    return pprPricing
      .filter((p) => p.ppr_type === "one_time" && p.price > 0)
      .map((p) => ({
        product: `ppr_${p.flight_type}`,
        price: p.price,
        name: p.pricing_name,
        description: getFlightTypeDescription(p.flight_type),
        type: "one_time",
        pricing_data: p,
      }));
  };

  const getFlightTypeDescription = (flightType) => {
    const descriptions = {
      single_day:
        "Typical for locals - One flying day out of Spilve with unlimited number of flights",
      single_arrival:
        "Typical for guests - valid also for a departure within 3 days starting with the day of arrival",
      single_departure:
        "Valid also for an arrival within 3 days starting with the day of departure",
      alternate: "Emergency/alternate landing permission",
    };
    return descriptions[flightType] || "Flight permission";
  };

  // Convert credit packages to display format
  const getSubscriptionPackages = () => {
    if (credits) return []; // Don't show subscriptions if user already has credits

    return creditPackages.map((pkg) => ({
      product: pkg.package_code,
      price: pkg.price,
      name: pkg.package_name,
      description: `${pkg.credit_amount} flight credits valid for ${
        pkg.duration_months
      } month${pkg.duration_months > 1 ? "s" : ""}`,
      type: "subscription",
      package_data: pkg,
    }));
  };

  const allProducts = [...getOneTimePurchases(), ...getSubscriptionPackages()];

  if (loadingPricing) {
    return <LoadingOverlay visible={true} overlayBlur={2} />;
  }

  return (
    <>
      <LoadingOverlay visible={visible} overlayBlur={2} />

      <Modal
        opened={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Payment Error"
        centered
      >
        <Alert icon={<IconAlertCircle size="1rem" />} color="red" mb="md">
          <Text size="sm">
            <strong>Payment Failed:</strong>{" "}
            {paymentError?.error || paymentError?.message}
          </Text>
        </Alert>

        {paymentError?.details && (
          <Text size="sm" color="dimmed" mb="md">
            {paymentError.details}
          </Text>
        )}

        {paymentError?.canPayOnSite && (
          <Alert icon={<IconInfoCircle size="1rem" />} color="blue">
            <Text size="sm">
              <strong>Alternative:</strong> You can visit the airport to
              purchase credits directly at the front desk.
            </Text>
          </Alert>
        )}

        <Group position="right" mt="md">
          <Button onClick={() => setShowErrorModal(false)}>Close</Button>
        </Group>
      </Modal>

      <Stack spacing="lg">
        <div>
          <Text size="xl" weight={700} mb="xs">
            Available Credits & Permissions
          </Text>
          <Text color="dimmed">
            Choose from one-time flight permissions or subscription packages
          </Text>
        </div>

        {/* One-Time PPR Purchases */}
        {getOneTimePurchases().length > 0 && (
          <>
            <div>
              <Group spacing="xs" mb="md">
                <IconPlane size={20} />
                <Text size="lg" weight={600}>
                  One-Time Flight Permissions
                </Text>
              </Group>

              <Grid>
                {getOneTimePurchases().map((item) => (
                  <Grid.Col lg={4} key={item.product}>
                    <Card withBorder radius="md" h="100%">
                      <Stack spacing="sm" h="100%">
                        <Group justify="space-between">
                          <Text fw={500} size="md">
                            {item.name}
                          </Text>
                          <Badge variant="outline" color="blue">
                            €{item.price}
                          </Badge>
                        </Group>

                        <Text fz="sm" c="dimmed" style={{ flexGrow: 1 }}>
                          {item.description}
                        </Text>

                        <Button
                          radius="xl"
                          loading={loadingPackage === item.product}
                          onClick={() =>
                            getPaymentLinkAndForward(item.price, item.product)
                          }
                          fullWidth
                        >
                          {loadingPackage === item.product
                            ? "Processing..."
                            : "Purchase"}
                        </Button>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </div>
          </>
        )}

        {/* Subscription Packages */}
        {getSubscriptionPackages().length > 0 && (
          <>
            <Divider />

            <div>
              <Group spacing="xs" mb="md">
                <IconCalendar size={20} />
                <Text size="lg" weight={600}>
                  Subscription Packages
                </Text>
              </Group>

              <Alert icon={<IconInfoCircle />} color="blue" mb="md">
                <Text size="sm">
                  <strong>Better Value:</strong> Subscription packages offer
                  multiple flight credits at a discounted rate compared to
                  individual purchases.
                </Text>
              </Alert>

              <Grid>
                {getSubscriptionPackages().map((item) => (
                  <Grid.Col lg={4} key={item.product}>
                    <Card
                      withBorder
                      radius="md"
                      h="100%"
                      style={{
                        border:
                          item.package_data?.duration_months === 6
                            ? "2px solid #228be6"
                            : undefined,
                      }}
                    >
                      <Stack spacing="sm" h="100%">
                        <Group justify="space-between">
                          <Text fw={500} size="md">
                            {item.name}
                          </Text>
                          <Badge variant="filled" color="green">
                            €{item.price}
                          </Badge>
                        </Group>

                        {item.package_data && (
                          <Group spacing="xl">
                            <div>
                              <Text size="xs" color="dimmed">
                                Credits
                              </Text>
                              <Text weight={600}>
                                {item.package_data.credit_amount}
                              </Text>
                            </div>
                            <div>
                              <Text size="xs" color="dimmed">
                                Per Credit
                              </Text>
                              <Text weight={600}>
                                €
                                {(
                                  item.price / item.package_data.credit_amount
                                ).toFixed(2)}
                              </Text>
                            </div>
                          </Group>
                        )}

                        <Text fz="sm" c="dimmed" style={{ flexGrow: 1 }}>
                          {item.description}
                        </Text>

                        {item.package_data?.duration_months === 6 && (
                          <Badge size="sm" color="blue" variant="light" mb="xs">
                            Most Popular
                          </Badge>
                        )}

                        <Button
                          radius="xl"
                          loading={loadingPackage === item.product}
                          onClick={() =>
                            getPaymentLinkAndForward(
                              item.price,
                              item.product,
                              item.package_data
                            )
                          }
                          fullWidth
                          variant={
                            item.package_data?.duration_months === 6
                              ? "filled"
                              : "light"
                          }
                        >
                          {loadingPackage === item.product
                            ? "Processing..."
                            : "Subscribe"}
                        </Button>
                      </Stack>
                    </Card>
                  </Grid.Col>
                ))}
              </Grid>
            </div>
          </>
        )}

        {allProducts.length === 0 && (
          <Alert icon={<IconInfoCircle />} color="yellow">
            <Text size="sm">
              No pricing packages are currently available. Please contact the
              airport administration.
            </Text>
          </Alert>
        )}
      </Stack>

      {paymentError && (
        <Alert icon={<IconAlertCircle size="1rem" />} color="orange" mt="md">
          <Text size="sm">
            <strong>Having payment issues?</strong> You can also purchase
            credits directly at Spilve Airport. Visit the front desk during
            operating hours.
          </Text>
        </Alert>
      )}
    </>
  );
};
